import { useSyncExternalStore } from 'react';
import type { RecentTable } from 'reducers/user';

import type { CheapestPoolTables } from 'components/LobbyLab/demoTables';
import type {
  ActiveTable,
  ActivityMetrics,
  CashGame,
  PlayerSummary
} from 'components/SaloonLobby/types';

/**
 * LOBBY LAB state — the design-engineering switchboard for the lobby.
 *
 * The lobby has states a designer cannot summon at will from a dev server:
 * a member header, a live promo, seated / queued tables in the Your Tables
 * strip, a recent-tables row, the activity metrics. This store lets the Lobby
 * Lab panel (./LobbyLab.tsx, lazy) switch each of them on with fixture data,
 * and lets SaloonLobbyContainer read the result. The panel writes here; the
 * container reads; nothing else touches it.
 *
 * Same two locks as the SFX Lab (see components/SfxLab/index.tsx for the long
 * form): LAB_AVAILABLE is an ALLOWLIST of lower environments (production ships
 * as both `production` and `live`, so a denylist would leak), and the operator
 * flag `localStorage.hijack_lobbylab = '1'` keeps it invisible until someone
 * asks for it. When either lock is shut, `getLobbyLab()` returns NEUTRAL and
 * the lobby renders exactly as a player sees it.
 *
 * The ACTIVITY STRIP was hidden in every BUILD by EN-10638 (owner, 2026-09-10)
 * while its numbers were a client-side estimate; EN-10417 (#3894) moved them to
 * the CDN activity document and the strip is on again. The switch lives in
 * SaloonLobbyContainer (`ACTIVITY_STRIP_ENABLED`); that is the ONE PLACE to
 * hide it. `activityStrip` below is a LOCAL DEV SERVER preview for when that
 * flag is off (owner, 2026-09-11, for a design demo): the container honours it
 * solely when NODE_ENV is 'development', which CRA sets for `yarn start` and
 * never for a build, so no deployed lobby acts on this state.
 *
 * Nothing else changes when the flag moves: ActivityStrip itself, its
 * collapsing row under the compact header, the pin chain beneath it, and the
 * filter bar's re-centring while the strip is absent are all still here and
 * still tested.
 */

export type RecentItem = { game: CashGame; recent: RecentTable };

export interface LobbyLabOverrides {
  player?: PlayerSummary;
  activeTables?: ActiveTable[];
  recentItems?: RecentItem[];
  /** LOCAL DEV SERVER ONLY: the strip's numbers for a demo (see `activityStrip`). */
  activity?: ActivityMetrics;
}

/**
 * Which copy the multi-run introduction preview shows: the timeless copy in
 * code on its own, or one edition of the `release.multi-run-intro` flag as
 * written (components/MultiRunIntro/multiRunIntroEditions.ts). The ids match
 * the flag's LaunchDarkly variations.
 */
export const MULTI_RUN_INTRO_PREVIEWS = ['off', 'timeless', 'nlhe-launch', 'omaha-live'] as const;
export type MultiRunIntroPreview = (typeof MULTI_RUN_INTRO_PREVIEWS)[number];

/**
 * Which copy the heads-up Sit & Go introduction preview shows (EN-10748): the
 * timeless copy in code on its own, or the edition of the `release.heads-up-intro`
 * flag as written (components/HeadsUpIntro/headsUpIntroEditions.ts).
 */
export const HEADS_UP_INTRO_PREVIEWS = ['off', 'timeless', 'heads-up-launch'] as const;
export type HeadsUpIntroPreview = (typeof HEADS_UP_INTRO_PREVIEWS)[number];

export interface LobbyLabState {
  /** Render the lobby (header, CTAs, tiles) as a member. The socket still holds a guest session. */
  member: boolean;
  /** LOCAL DEV SERVER ONLY: show the hidden Activity Strip for a demo (see the note above). */
  activityStrip: boolean;
  /**
   * Light the promo banner. Not an override: PromoStrip renders InlinePromoBar,
   * which reads Redux `playerPromos` directly, so the panel seeds a fixture
   * Promotion into the store while this is on (see LobbyLab.tsx).
   */
  promo: boolean;
  /**
   * Show production's cash stake ladder, nine tables to a stake, built from the
   * live list (see demoTables.ts). A lower environment runs one table at a
   * stake — an empty room in a demo — on a ladder that is not production's.
   */
  demoTables: boolean;
  /** A busy night: half the tables in every stake full and waitlisted (demoTables.ts). */
  highTraffic: boolean;
  /**
   * Show the maintenance marquee with fixture copy. A PREVIEW of the bar only:
   * it does not put the lobby in maintenance, so tables stay lit and clickable
   * (the real slice dims and disables them). A genuine notice always wins over
   * this — see MaintenanceMarquee's `previewMessage`.
   */
  maintenanceMarquee: boolean;
  /**
   * Preview the one-time multi-run introduction (EN-10791) as a player sees it,
   * on demand, in any of its editions — without the LaunchDarkly flag, without
   * the once-per-player gate, and without writing the player's "seen" key:
   * dismissing the preview just switches this back to 'off'. The real trigger
   * is MultiRunIntroAutoPrompt.
   */
  multiRunIntro: MultiRunIntroPreview;
  /** The heads-up Sit & Go introduction (EN-10748), previewed the same way. */
  headsUpIntro: HeadsUpIntroPreview;
  /**
   * The NEW badge the Sit & Go tab wears while the heads-up introduction's
   * flag is on (EN-10748), without the flag, so it can be looked at on its
   * own, after the modal has been dismissed.
   */
  headsUpNewBadge: boolean;
  /**
   * With demo tables on, how many tables the cheapest Sit & Go pool carries —
   * one open, the rest full — so the ladder's wrapping can be watched under a
   * taller column (owner, 2026-09-10). Production shows one.
   */
  sitgoCheapestTables: CheapestPoolTables;
  overrides: LobbyLabOverrides;
}

const OVERRIDE_KEYS: (keyof LobbyLabOverrides)[] = [
  'player',
  'activeTables',
  'recentItems',
  'activity'
];

const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
export const LAB_AVAILABLE =
  process.env.NODE_ENV !== 'production' || /^(develop|test|staging)/.test(stage);

/**
 * The maintenance marquee's preview copy. That bar only renders when the engine
 * publishes a maintenance window, so there is otherwise no way to look at it on
 * demand — which is how its stale width cap went unseen (owner, 2026-09-17).
 *
 * It lives HERE rather than in fixtures.ts because SaloonLobbyContainer reads it
 * and already imports this module; fixtures.ts is reached only through the lazy
 * panel, and pulling it into the main chunk would undo what EN-10750 measured.
 *
 * Reads as a fixture on sight, like every other one: nobody who wanders past a
 * laptop with the Lab open should think the room is going down.
 */
export const LAB_MAINTENANCE_NOTICE =
  'LOBBY LAB PREVIEW — this is a sample maintenance notice, not a real one. Scheduled maintenance 3:00 AM – 4:00 AM CT.';

export const LOBBY_LAB_FLAG_KEY = 'hijack_lobbylab';
const STATE_KEY = 'hijack_lobbylab_state';

export const isLobbyLabOn = (): boolean => {
  if (!LAB_AVAILABLE) return false;
  try {
    return localStorage.getItem(LOBBY_LAB_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

const NEUTRAL: LobbyLabState = Object.freeze({
  member: false,
  activityStrip: false,
  promo: false,
  demoTables: false,
  highTraffic: false,
  maintenanceMarquee: false,
  multiRunIntro: 'off',
  headsUpIntro: 'off',
  headsUpNewBadge: false,
  sitgoCheapestTables: 1,
  overrides: {}
}) as LobbyLabState;

const isObject = (v: unknown): boolean => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
const isObjectArray = (v: unknown): boolean => Array.isArray(v) && v.every(isObject);

/**
 * What each override has to LOOK like to be believed. localStorage is editable
 * by hand and survives every deploy, so a value written by an older panel — or
 * by a curious person — arrives here shaped however it likes. Handing a
 * non-array `activeTables` to the lobby took the tab down on the first
 * `.filter`, and the only way out was clearing storage for the origin, which
 * is precisely the trap the SFX Lab's missing escape hatch set (review,
 * 2026-09-12). These are shallow checks on purpose: enough that the lobby's own
 * code paths hold, not a schema.
 */
const OVERRIDE_SHAPE: Record<keyof LobbyLabOverrides, (v: unknown) => boolean> = {
  player: isObject,
  activeTables: isObjectArray,
  recentItems: (v) =>
    Array.isArray(v) &&
    v.every(
      (e) => isObject(e) && isObject((e as RecentItem).game) && isObject((e as RecentItem).recent)
    ),
  activity: isObject
};

/**
 * Keep only the override keys this build knows AND whose stored value is the
 * right shape — stale keys from an older panel are dropped, and so is anything
 * malformed, leaving the authoritative live data in its place.
 */
const pickOverrides = (raw: unknown): LobbyLabOverrides => {
  if (!isObject(raw)) return {};
  const out: LobbyLabOverrides = {};
  OVERRIDE_KEYS.forEach((k) => {
    const v = (raw as Record<string, unknown>)[k];
    if (v !== undefined && OVERRIDE_SHAPE[k](v)) (out as Record<string, unknown>)[k] = v;
  });
  return out;
};

const readStored = (): LobbyLabState => {
  if (!isLobbyLabOn()) return NEUTRAL;
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return NEUTRAL;
    const parsed = JSON.parse(raw) as Partial<LobbyLabState>;
    return {
      member: typeof parsed.member === 'boolean' ? parsed.member : NEUTRAL.member,
      activityStrip:
        typeof parsed.activityStrip === 'boolean' ? parsed.activityStrip : NEUTRAL.activityStrip,
      promo: typeof parsed.promo === 'boolean' ? parsed.promo : NEUTRAL.promo,
      demoTables: typeof parsed.demoTables === 'boolean' ? parsed.demoTables : NEUTRAL.demoTables,
      highTraffic:
        typeof parsed.highTraffic === 'boolean' ? parsed.highTraffic : NEUTRAL.highTraffic,
      maintenanceMarquee:
        typeof parsed.maintenanceMarquee === 'boolean'
          ? parsed.maintenanceMarquee
          : NEUTRAL.maintenanceMarquee,
      multiRunIntro: (MULTI_RUN_INTRO_PREVIEWS as readonly unknown[]).includes(parsed.multiRunIntro)
        ? (parsed.multiRunIntro as MultiRunIntroPreview)
        : NEUTRAL.multiRunIntro,
      headsUpIntro: (HEADS_UP_INTRO_PREVIEWS as readonly unknown[]).includes(parsed.headsUpIntro)
        ? (parsed.headsUpIntro as HeadsUpIntroPreview)
        : NEUTRAL.headsUpIntro,
      headsUpNewBadge:
        typeof parsed.headsUpNewBadge === 'boolean'
          ? parsed.headsUpNewBadge
          : NEUTRAL.headsUpNewBadge,
      sitgoCheapestTables:
        parsed.sitgoCheapestTables === 2 || parsed.sitgoCheapestTables === 3
          ? parsed.sitgoCheapestTables
          : NEUTRAL.sitgoCheapestTables,
      overrides: pickOverrides(parsed.overrides)
    };
  } catch {
    return NEUTRAL;
  }
};

const PRESET_KEY = 'hijack_lobbylab_preset';

/**
 * URL switch, so nobody has to open DevTools to reach the lab:
 *   ?lobbylab=1     turn the lab on (sticks in this browser, like the flag)
 *   ?lobbylab=all   turn it on AND light every switch with fixtures, once
 *   ?lobbylab=off   turn it off and forget its state
 *   anything else   no-op — the parameter is stripped and nothing changes
 * Read once at boot; the parameter is then removed from the address bar so a
 * plain reload does not re-apply it. Locked builds ignore it entirely.
 */
const adoptLobbyLabQuery = (): void => {
  if (!LAB_AVAILABLE || typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const value = url.searchParams.get('lobbylab');
    if (value === null) return;
    const wanted = value.toLowerCase();
    // ONLY the documented values act, exactly as the SFX Lab's switch does
    // (utils/sfxTrace.ts). The `else` this replaces enabled the lab for ANY
    // unrecognised value — a bare `?lobbylab=`, a typo, `?lobbylab=false` —
    // and, worse than its sibling, also left whatever fixtures were last
    // stored in place. Caught reviewing the fix to the other lab, which had
    // claimed to mirror this one (review, 2026-09-13).
    if (wanted === 'off' || wanted === '0') {
      localStorage.removeItem(LOBBY_LAB_FLAG_KEY);
      localStorage.removeItem(STATE_KEY);
      localStorage.removeItem(PRESET_KEY);
    } else if (wanted === 'on' || wanted === '1' || wanted === 'all') {
      localStorage.setItem(LOBBY_LAB_FLAG_KEY, '1');
      if (wanted === 'all') localStorage.setItem(PRESET_KEY, 'all');
    }
    url.searchParams.delete('lobbylab');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* noop — the lab is a convenience, never a dependency */
  }
};

/** The panel calls this once on mount: the pending preset, cleared on read. */
export const consumeLobbyLabPreset = (): 'all' | null => {
  try {
    const preset = localStorage.getItem(PRESET_KEY);
    if (preset !== null) localStorage.removeItem(PRESET_KEY);
    return preset === 'all' ? 'all' : null;
  } catch {
    return null;
  }
};

adoptLobbyLabQuery();
let current: LobbyLabState = readStored();
const listeners = new Set<() => void>();

const persist = (state: LobbyLabState) => {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* noop — the lab is a convenience, never a dependency */
  }
};

const emit = () => listeners.forEach((l) => l());

export const getLobbyLab = (): LobbyLabState => current;

export const subscribeLobbyLab = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Merge a patch into the state. `overrides` merges one level deep; pass `undefined` for a key to clear it. */
export const setLobbyLab = (patch: Partial<LobbyLabState>): void => {
  if (!isLobbyLabOn()) return;
  const overrides = { ...current.overrides, ...(patch.overrides ?? {}) };
  (Object.keys(overrides) as (keyof LobbyLabOverrides)[]).forEach((k) => {
    if (overrides[k] === undefined) delete overrides[k];
  });
  current = { ...current, ...patch, overrides };
  persist(current);
  emit();
};

export const resetLobbyLab = (): void => {
  if (!isLobbyLabOn()) return;
  current = { ...NEUTRAL, overrides: {} };
  try {
    localStorage.removeItem(STATE_KEY);
  } catch {
    /* noop */
  }
  emit();
};

/** Flip the operator flag (the panel's own on/off). Takes effect after a reload, like the SFX Lab. */
export const setLobbyLabFlag = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(LOBBY_LAB_FLAG_KEY, '1');
    else localStorage.removeItem(LOBBY_LAB_FLAG_KEY);
  } catch {
    /* noop */
  }
};

/** Reactive read — NEUTRAL wherever the lab is locked, so production never sees a fixture. */
export const useLobbyLab = (): LobbyLabState =>
  useSyncExternalStore(subscribeLobbyLab, getLobbyLab, getLobbyLab);

/** Test seam: re-read storage (jest runs share the module). */
export const __reloadLobbyLabForTests = (): void => {
  current = readStored();
  emit();
};
